import React, { useEffect, useState } from 'react';
import { useFile } from '../context/filecontext';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';

// Notification types configuration
const NOTIFICATION_TYPES = {
  warning: {
    icon: AlertCircle,
    bgColor: 'bg-yellow-50',
    iconColor: 'text-yellow-400',
    iconBg: 'bg-yellow-100',
    progressColor: 'bg-yellow-500'
  },
  error: {
    icon: X,
    bgColor: 'bg-red-50',
    iconColor: 'text-red-400',
    iconBg: 'bg-red-100',
    progressColor: 'bg-red-500'
  },
  success: {
    icon: CheckCircle,
    bgColor: 'bg-cyan-50',
    iconColor: 'text-cyan-400',
    iconBg: 'bg-cyan-100',
    progressColor: 'bg-cyan-500'
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-100',
    progressColor: 'bg-blue-500'
  }
};

const Notification = () => {
  const { showNotification, setShowNotification, notificationConfig } = useFile();
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  
  // Get notification type styling or default to warning
  const type = notificationConfig?.type || 'warning';
  const message = notificationConfig?.message || 'Please upload a file first';
  const duration = notificationConfig?.duration || 5000;
  const NotificationIcon = NOTIFICATION_TYPES[type]?.icon || AlertCircle;

  useEffect(() => {
    let timer;
    let progressTimer;

    if (showNotification) {
      setIsVisible(true);
      setProgress(100);

      // Progress bar animation
      progressTimer = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - (100 / (duration / 25));
          return Math.max(newProgress, 0);
        });
      }, 25);

      // Auto dismiss timer
      timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => {
          setShowNotification(false);
        }, 300);
      }, duration);
    }

    return () => {
      clearTimeout(timer);
      clearInterval(progressTimer);
    };
  }, [showNotification, setShowNotification, duration]);

  if (!showNotification) return null;

  return (
    <div
      className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
    >
      <div className={`relative rounded-lg shadow-lg overflow-hidden ${NOTIFICATION_TYPES[type].bgColor}`}>
        <div className="p-4">
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className={`h-10 w-10 rounded-lg ${NOTIFICATION_TYPES[type].iconBg} flex items-center justify-center`}>
                <NotificationIcon className={`h-5 w-5 ${NOTIFICATION_TYPES[type].iconColor}`} />
              </div>
            </div>

            {/* Message */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {message}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => setShowNotification(false), 300);
              }}
              className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-lg hover:bg-black/5 transition-colors"
            >
              <span className="sr-only">Close</span>
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/5">
          <div
            className={`h-full ${NOTIFICATION_TYPES[type].progressColor} transition-all duration-75 ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default Notification;